
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { useEffect, useRef } from "react";

const Miniservices = ({ showTitle = true }) => {
    const { services } = useI18n();
    const sectionRef = useRef(null);

    useEffect(() => {
        const section = sectionRef.current;

        if (!section) return;

        const elements = section.querySelectorAll("[data-reveal]");

        // При отключённой поддержке IntersectionObserver
        // сразу показываем все элементы.
        if (!("IntersectionObserver" in window)) {
            elements.forEach((element) => {
                element.classList.add("is-visible");
            });

            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;

                    entry.target.classList.add("is-visible");
                    observer.unobserve(entry.target);
                });
            },
            {
                threshold: 0,
                rootMargin: "0px 0px 80px 0px",
            }
        );

        elements.forEach((element) => observer.observe(element));

        return () => observer.disconnect();
    }, [services.items, showTitle]);

    return (
        <section
            ref={sectionRef}
            className="relative isolate overflow-hidden  px-6 py-20 text-slate-950 md:px-12 md:py-28"
        >
            {/* Static abstract background */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
            >
                <div className="absolute -right-40 top-0 h-96 w-96 rounded-full bg-sky-100/70" />

                <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-blue-100/50" />

                <div
                    className="absolute inset-0 opacity-[0.025]"
                    style={{
                        backgroundImage:
                            "linear-gradient(to right, #334155 1px, transparent 1px), linear-gradient(to bottom, #334155 1px, transparent 1px)",
                        backgroundSize: "50px 50px",
                    }}
                />
            </div>

            <div className="relative mx-auto max-w-7xl">
                {/* Section heading */}
                {showTitle && (
                    <div
                        data-reveal
                        className="services-reveal mb-10 flex items-end justify-between gap-6 md:mb-14"
                    >
                        <div>
                            <span className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-sky-700 sm:text-sm">
                                <span className="h-px w-8 bg-sky-500" />
                                Our services
                            </span>

                            <h2 className="max-w-2xl text-3xl font-extrabold tracking-tight text-slate-900 md:text-5xl">
                                {services.title}
                            </h2>
                        </div>

                        <span
                            aria-hidden="true"
                            className="hidden h-px flex-1 bg-gradient-to-r from-sky-400/70 to-transparent md:block"
                        />
                    </div>
                )}

                {/* Service cards */}
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {services.items.map((service, index) => (
                        <Link
                            key={service.id}
                            to={`/${service.slug ?? service.id}`}
                            aria-label={service.name}
                            data-reveal
                            className="
                                services-reveal services-card
                                group relative isolate block
                                h-[300px] overflow-hidden rounded-2xl
                                bg-slate-900
                                outline-none
                                focus-visible:ring-2
                                focus-visible:ring-sky-500
                                focus-visible:ring-offset-4
                            "
                        >
                            {/* Image */}
                            <img
                                src={service.photo}
                                alt=""
                                aria-hidden="true"
                                loading={index < 3 ? "eager" : "lazy"}
                                decoding="async"
                                fetchPriority={index === 0 ? "high" : "auto"}
                                className="
                                    services-card-image
                                    absolute inset-0 h-full w-full
                                    object-cover
                                "
                            />

                            {/* Dark gradient */}
                            <div
                                aria-hidden="true"
                                className="
                                    absolute inset-0
                                    bg-gradient-to-t
                                    from-black/90 via-black/35 to-black/5
                                "
                            />

                            {/* Hover overlay */}
                            <div
                                aria-hidden="true"
                                className="
                                    services-card-overlay
                                    pointer-events-none absolute inset-0
                                    bg-gradient-to-tr
                                    from-sky-500/20 via-transparent to-transparent
                                "
                            />

                            {/* Border */}
                            <div
                                aria-hidden="true"
                                className="
                                    pointer-events-none absolute inset-0
                                    rounded-2xl border border-white/0
                                    transition-colors duration-200
                                    group-hover:border-white/30
                                "
                            />

                            {/* Card content */}
                            <div
                                className="
                                    absolute inset-x-0 bottom-0 p-6 sm:p-7
                                "
                            >
                                {/* Accent line */}
                                <div
                                    aria-hidden="true"
                                    className="
                                        mb-4 h-1 w-10 rounded-full
                                        bg-sky-400
                                        transition-[width] duration-200
                                        group-hover:w-16
                                    "
                                />

                                {/* Title */}
                                <h3
                                    className="
                                        mb-3 text-2xl font-bold leading-tight
                                        text-white sm:text-3xl
                                    "
                                >
                                    {service.name}
                                </h3>

                                {/* Description */}
                                <p className="line-clamp-3 max-w-xl text-sm leading-6 text-white/75 sm:text-base">
                                    {service.info}
                                </p>

                                {/* Details */}
                                <div
                                    className="
                                        mt-5 flex items-center gap-2
                                        text-sm font-semibold text-sky-300
                                        transition-colors duration-200
                                        group-hover:text-sky-200
                                    "
                                >
                                    <span>{services.details}</span>

                                    <svg
                                        aria-hidden="true"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        className="
                                            h-4 w-4
                                            transition-transform duration-200
                                            group-hover:translate-x-1
                                        "
                                    >
                                        <path
                                            d="M5 12h14m-6-6 6 6-6 6"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Smooth, lightweight animations */}
            <style>{`
                .services-reveal {
                    opacity: 0;
                    transition: opacity 300ms ease-out;
                }

                .services-reveal.is-visible {
                    opacity: 1;
                }

                /* Image zoom only on desktop-like devices */
                .services-card-image {
                    transition: transform 350ms ease-out;
                }

                .services-card-overlay {
                    opacity: 0;
                    transition: opacity 250ms ease-out;
                }

                @media (hover: hover) and (pointer: fine) {
                    .services-card:hover .services-card-image {
                        transform: scale(1.035);
                    }

                    .services-card:hover .services-card-overlay {
                        opacity: 1;
                    }
                }

                /* Prevent animations from interfering with scrolling */
                @media (prefers-reduced-motion: reduce) {
                    .services-reveal,
                    .services-card-image,
                    .services-card-overlay,
                    .services-card * {
                        animation: none !important;
                        transition: none !important;
                    }

                    .services-reveal {
                        opacity: 1;
                    }
                }
            `}</style>
        </section>
    );
};

export default Miniservices;

